/**
 * G21 isolated center-band cinematic zoom.
 * Adds a synchronized overlay only to the middle third of the existing product video.
 * Existing gallery, thumbnails, playback-rate logic and crossfade loop remain untouched.
 */
(() => {
  'use strict';

  if (window.G21ProductCenterBandZoomLoaded) return;
  window.G21ProductCenterBandZoomLoaded = true;

  const HOST_SELECTOR = '.g21-video-player-stack';
  const LAYER_CLASS = 'g21-center-band-zoom-layer';
  const VIDEO_CLASS = 'g21-center-band-zoom-video';
  const EFFECT_START = 0.4;
  const ZOOM_PEAK = 0.5;
  const EFFECT_END = 0.6;
  const MAX_ZOOM = 2;
  const SOURCE_SYNC_THRESHOLD = 0.18;
  const PERIODIC_SYNC_MS = 220;

  const clamp01 = (value) => Math.min(1, Math.max(0, value));

  const smootherstep01 = (value) => {
    const x = clamp01(value);
    return x * x * x * ((x * ((x * 6) - 15)) + 10);
  };

  const readOpacity = (element) => {
    const value = Number.parseFloat(window.getComputedStyle(element).opacity);
    return Number.isFinite(value) ? value : 0;
  };

  const getBaseVideos = (host) =>
    Array.from(host.children).filter((child) =>
      child instanceof HTMLVideoElement && !child.classList.contains(VIDEO_CLASS)
    );

  const getDominantVideo = (videos, current) => {
    if (!videos.length) return null;
    return videos.reduce((best, candidate) => {
      const candidateOpacity = readOpacity(candidate);
      const bestOpacity = best ? readOpacity(best) : -1;
      if (candidateOpacity > bestOpacity + 0.02) return candidate;
      return best;
    }, current && videos.includes(current) ? current : videos[0]);
  };

  const getSource = (video) =>
    video.currentSrc || video.src || video.querySelector('source[src]')?.src || '';

  const getZoomState = (progress) => {
    if (progress < EFFECT_START || progress > EFFECT_END) {
      return { active: false, scale: 1, alpha: 0 };
    }

    let scale = 1;
    if (progress <= ZOOM_PEAK) {
      const local = (progress - EFFECT_START) / (ZOOM_PEAK - EFFECT_START);
      scale = 1 + ((MAX_ZOOM - 1) * smootherstep01(local));
    } else {
      const local = (progress - ZOOM_PEAK) / (EFFECT_END - ZOOM_PEAK);
      scale = MAX_ZOOM - ((MAX_ZOOM - 1) * smootherstep01(local));
    }

    const fadeIn = clamp01((progress - EFFECT_START) / 0.008);
    const fadeOut = clamp01((EFFECT_END - progress) / 0.008);

    return {
      active: true,
      scale,
      alpha: Math.min(fadeIn, fadeOut)
    };
  };

  const configureOverlayVideo = (video) => {
    video.className = VIDEO_CLASS;
    video.muted = true;
    video.defaultMuted = true;
    video.controls = false;
    video.playsInline = true;
    video.preload = 'auto';
    video.disablePictureInPicture = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('aria-hidden', 'true');
    video.tabIndex = -1;
    return video;
  };

  const setupHost = (host) => {
    if (!(host instanceof HTMLElement) || host.dataset.g21CenterBandZoomReady === 'true') return;

    const baseVideos = getBaseVideos(host);
    if (!baseVideos.length) return;

    const layer = document.createElement('div');
    layer.className = LAYER_CLASS;
    layer.setAttribute('aria-hidden', 'true');

    const overlay = configureOverlayVideo(document.createElement('video'));
    layer.append(overlay);
    host.append(layer);

    host.dataset.g21CenterBandZoomReady = 'true';
    host.classList.add('g21-center-band-zoom-active');

    let dominant = null;
    let overlaySource = '';
    let rafId = 0;
    let lastSyncAt = 0;
    let overlayPlayPending = false;

    const hideOverlay = () => {
      layer.style.opacity = '0';
      overlay.style.transform = 'translate3d(0, 0, 0) scale(1)';
      if (!overlay.paused) overlay.pause();
    };

    const loadSource = (source, base) => {
      if (!source || source === overlaySource) return;
      overlaySource = source;
      overlay.pause();
      overlay.removeAttribute('src');
      overlay.load();
      overlay.src = source;
      overlay.load();

      const syncAfterMetadata = () => {
        try {
          if (Number.isFinite(base.currentTime)) overlay.currentTime = base.currentTime;
        } catch (_) {}
      };

      if (overlay.readyState >= 1) syncAfterMetadata();
      else overlay.addEventListener('loadedmetadata', syncAfterMetadata, { once: true });
    };

    const syncOverlay = (base, now, force = false) => {
      const source = getSource(base);
      loadSource(source, base);

      if (!source || overlay.readyState < 1) return false;

      const baseRate = Number.isFinite(base.playbackRate) ? base.playbackRate : 1;
      if (Math.abs(overlay.playbackRate - baseRate) > 0.01) {
        overlay.playbackRate = baseRate;
        overlay.defaultPlaybackRate = baseRate;
      }

      if (force || now - lastSyncAt >= PERIODIC_SYNC_MS) {
        lastSyncAt = now;
        const drift = Math.abs((overlay.currentTime || 0) - (base.currentTime || 0));
        if (force || drift > SOURCE_SYNC_THRESHOLD) {
          try { overlay.currentTime = base.currentTime; } catch (_) {}
        }
      }

      return true;
    };

    const ensureOverlayPlaying = async () => {
      if (!overlay.paused || overlayPlayPending) return;
      overlayPlayPending = true;
      try { await overlay.play(); } catch (_) {}
      overlayPlayPending = false;
    };

    const render = (now) => {
      if (!host.isConnected) return;

      const videos = getBaseVideos(host);
      dominant = getDominantVideo(videos, dominant);

      if (!(dominant instanceof HTMLVideoElement)) {
        hideOverlay();
        rafId = requestAnimationFrame(render);
        return;
      }

      const duration = dominant.duration;
      const currentTime = dominant.currentTime;
      const visibleOpacity = readOpacity(dominant);

      if (
        document.hidden ||
        dominant.paused ||
        dominant.ended ||
        !Number.isFinite(duration) ||
        duration <= 0 ||
        !Number.isFinite(currentTime) ||
        visibleOpacity <= 0.01
      ) {
        hideOverlay();
        rafId = requestAnimationFrame(render);
        return;
      }

      const progress = clamp01(currentTime / duration);
      const state = getZoomState(progress);

      if (!state.active || state.alpha <= 0.001) {
        hideOverlay();
        rafId = requestAnimationFrame(render);
        return;
      }

      const ready = syncOverlay(dominant, now, overlaySource !== getSource(dominant));
      if (!ready) {
        hideOverlay();
        rafId = requestAnimationFrame(render);
        return;
      }

      ensureOverlayPlaying();
      overlay.style.transform = `translate3d(0, 0, 0) scale(${state.scale.toFixed(5)})`;
      layer.style.opacity = String(Math.min(1, visibleOpacity * state.alpha));

      rafId = requestAnimationFrame(render);
    };

    const forceSync = () => {
      const videos = getBaseVideos(host);
      dominant = getDominantVideo(videos, dominant);
      if (dominant) syncOverlay(dominant, performance.now(), true);
    };

    const bindBaseEvents = (video) => {
      if (video.dataset.g21CenterBandZoomBound === 'true') return;
      video.dataset.g21CenterBandZoomBound = 'true';
      ['loadedmetadata', 'loadeddata', 'canplay', 'seeking', 'seeked', 'ratechange', 'play'].forEach((eventName) => {
        video.addEventListener(eventName, forceSync, { passive: true });
      });
      video.addEventListener('pause', () => {
        if (dominant === video) hideOverlay();
      }, { passive: true });
    };

    baseVideos.forEach(bindBaseEvents);

    const observer = new MutationObserver(() => {
      getBaseVideos(host).forEach(bindBaseEvents);
    });
    observer.observe(host, { childList: true, subtree: false, attributes: true, attributeFilter: ['src', 'style'] });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) hideOverlay();
      else forceSync();
    });

    rafId = requestAnimationFrame(render);

    host.addEventListener('g21:center-band-zoom:destroy', () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
      overlay.pause();
      layer.remove();
      delete host.dataset.g21CenterBandZoomReady;
    }, { once: true });
  };

  const setup = (scope = document) => {
    if (scope instanceof Element && scope.matches(HOST_SELECTOR)) setupHost(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(HOST_SELECTOR).forEach(setupHost);
  };

  let scheduled = false;
  const scheduleSetup = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      setup(document);
    });
  };

  const start = () => {
    setup(document);
    [150, 500, 1200, 2500].forEach((delay) => window.setTimeout(() => setup(document), delay));

    const observer = new MutationObserver((records) => {
      const relevant = records.some((record) =>
        Array.from(record.addedNodes).some((node) =>
          node instanceof Element &&
          (node.matches?.(HOST_SELECTOR) || node.querySelector?.(HOST_SELECTOR))
        )
      );
      if (relevant) scheduleSetup();
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => setup(event.target));
})();
